import React, { useRef, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSafeSelectHandler, useSafeDOMUpdate } from '@/hooks/useResizeObserverFix';

// Composant Select avec gestion technique appropriée des ResizeObserver
export const SafeSelect = ({ children, onValueChange, value, ...props }) => {
  const selectRef = useRef(null);
  const safeSelectHandler = useSafeSelectHandler(onValueChange);
  const safeDOMUpdate = useSafeDOMUpdate();
  
  // Gestion sécurisée des changements de valeur
  const handleValueChange = React.useCallback((newValue) => {
    // Utiliser le handler sécurisé qui implémente requestAnimationFrame et batching
    safeSelectHandler(newValue);
  }, [safeSelectHandler]);

  // Gestion des mutations DOM lors du rendu
  useEffect(() => {
    if (selectRef.current) {
      safeDOMUpdate(() => {
        // Toute manipulation DOM nécessaire après le rendu
        // est différée pour éviter les conflits ResizeObserver
      });
    }
  }, [value, safeDOMUpdate]);

  // Observer les changements de taille de manière sécurisée
  useEffect(() => {
    const element = selectRef.current;
    if (!element || !window.ResizeObserver) return;

    let rafId = null;
    const observer = new ResizeObserver((entries) => {
      // Annuler le frame précédent s'il existe
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      // Différer le traitement au prochain frame
      rafId = requestAnimationFrame(() => {
        // Traitement différé et sécurisé des changements de taille
        rafId = null;
      });
    });

    // Observer avec gestion d'erreur
    try {
      observer.observe(element);
    } catch (error) {
      // Ignorer les erreurs d'observation
    }

    return () => {
      // Cleanup approprié
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      try {
        observer.disconnect();
      } catch (error) {
        // Ignorer les erreurs de cleanup
      }
    };
  }, []);

  return (
    <div ref={selectRef}>
      <Select 
        value={value} 
        onValueChange={handleValueChange}
        {...props}
      >
        {children}
      </Select>
    </div>
  );
};

// Wrapper pour SelectTrigger avec gestion des mutations DOM
export const SafeSelectTrigger = ({ children, ...props }) => {
  const triggerRef = useRef(null);
  const safeDOMUpdate = useSafeDOMUpdate();

  useEffect(() => {
    if (triggerRef.current) {
      safeDOMUpdate(() => {
        // Différer toute manipulation DOM nécessaire
      });
    }
  }, [safeDOMUpdate]);

  return (
    <div ref={triggerRef}>
      <SelectTrigger {...props}>
        {children}
      </SelectTrigger>
    </div>
  );
};

// Wrapper pour SelectContent avec gestion des portals
export const SafeSelectContent = ({ children, ...props }) => {
  const contentRef = useRef(null);
  const safeDOMUpdate = useSafeDOMUpdate();

  useEffect(() => {
    // Différer la création du portal pour éviter les mutations immédiates
    safeDOMUpdate(() => {
      // Le contenu du Select est généralement rendu dans un portal
      // Cette différenciation évite les conflits avec ResizeObserver
    });
  }, [safeDOMUpdate]);

  return (
    <SelectContent ref={contentRef} {...props}>
      {children}
    </SelectContent>
  );
};

export { SelectItem, SelectValue };
export { SafeSelectTrigger as SelectTrigger, SafeSelectContent as SelectContent };
export default SafeSelect;